import { Badge } from "@/components/ui/badge";
import {
	formatMessage,
	reconciliationMessages,
	reconciliationResultMessages,
} from "@/services/messages";
import { useAuthStore } from "@/stores/auth-store";
import type { ReconciliationMode, RunRecord } from "@efes/shared";
import { DownloadPdfButton } from "./DownloadPdfButton";
import type { SeverityCounters } from "./lib/view-model";

/**
 * Сводка прогона сверху отчёта (FR-017, FR-018).
 *
 * Содержит:
 * - имя набора, level, modes
 * - дата прогона, длительность, автор
 * - totals по severity (с фильтр-счётчиком, если применимо)
 *
 * При активных фильтрах (счётчик `shown` < `total`) добавляется строка
 * «Показано N из M». Метаданные не меняются.
 */
export type ResultLayout = "feed" | "master-detail";

export interface ResultBannerProps {
	run: RunRecord;
	setName: string;
	counters: SeverityCounters;
	/** Если фильтры активны и `shown < counters.total`, показываем дополнительно «Показано N из M». */
	shownCount?: number;
	/** Текущий layout. Если не передан — toggle не рендерится. */
	layout?: ResultLayout;
	/** Колбэк смены layout. Должен передаваться вместе с `layout`. */
	onLayoutChange?: (layout: ResultLayout) => void;
}

// Sprint 3 / A4: legacy `aggregate-by-work-kind` удалён.
const LEVEL_LABEL_KEY: Record<string, keyof typeof reconciliationMessages> = {
	position: "level_position",
	"grand-total": "level_grand_total",
	unified: "level_unified",
};

const MODE_LABEL_KEY: Record<ReconciliationMode, keyof typeof reconciliationMessages> = {
	volume: "mode_volume",
	rate: "mode_rate",
	cost: "mode_cost",
};

function formatDateTime(value: string | null): string {
	if (!value) return "—";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** Форматирует длительность в «X сек» или «M мин Y сек». */
export function formatDuration(startedAt: string, completedAt: string | null): string {
	if (!completedAt) return "—";
	const startMs = Date.parse(startedAt);
	const endMs = Date.parse(completedAt);
	if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return "—";
	const totalSeconds = Math.round((endMs - startMs) / 1000);
	const banner = reconciliationResultMessages.banner;
	if (totalSeconds < 60) {
		return formatMessage(banner.duration_seconds_template, { seconds: totalSeconds });
	}
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return formatMessage(banner.duration_minutes_template, { minutes, seconds });
}

export function ResultBanner({
	run,
	setName,
	counters,
	shownCount,
	layout,
	onLayoutChange,
}: ResultBannerProps) {
	const banner = reconciliationResultMessages.banner;
	const redesign = reconciliationResultMessages.redesign;
	const currentUser = useAuthStore((state) => state.user);
	const level = run.parametersSnapshot.set.level;
	const modes = run.modes;
	const userName = resolveUserName(run, currentUser);
	const isFiltered = shownCount !== undefined && shownCount !== counters.total;

	return (
		<section
			aria-label={banner.set_name_label}
			className="rounded-lg border border-slate-200 bg-white p-4"
		>
			<header className="mb-3 flex items-baseline justify-between gap-3">
				<h2 className="text-lg font-semibold text-slate-900">{setName}</h2>
				<div className="flex items-center gap-3">
					<DownloadPdfButton runId={run.id} runStatus={run.status} />
					<span className="text-xs text-slate-500">{formatDateTime(run.startedAt)}</span>
				</div>
			</header>

			<dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
				<MetaItem label={banner.level_label}>
					{reconciliationMessages[LEVEL_LABEL_KEY[level] ?? "level_unified"]}
				</MetaItem>
				<MetaItem label={banner.modes_label}>
					<div className="flex flex-wrap gap-1">
						{modes.map((m) => (
							<Badge key={m} variant="pill" tone="neutral">
								{reconciliationMessages[MODE_LABEL_KEY[m]]}
							</Badge>
						))}
					</div>
				</MetaItem>
				<MetaItem label={banner.duration_label}>
					{formatDuration(run.startedAt, run.completedAt)}
				</MetaItem>
				<MetaItem label={banner.author_label}>{userName}</MetaItem>
			</dl>

			<div className="mt-3 flex flex-wrap items-baseline gap-3 border-t border-slate-100 pt-3">
				<span className="text-xs font-medium uppercase tracking-wide text-slate-500">
					{banner.totals_label}:
				</span>
				<span className="text-sm font-semibold text-slate-900 tabular-nums">
					{formatMessage(banner.totals_template, {
						total: counters.total,
						error: counters.error,
						warning: counters.warning,
					})}
				</span>
				{isFiltered && (
					<span className="text-xs text-blue-600 tabular-nums">
						{formatMessage(banner.shown_template, {
							shown: shownCount ?? counters.total,
							total: counters.total,
						})}
					</span>
				)}
				{layout && onLayoutChange ? (
					<div className="ml-auto inline-flex items-center gap-1.5">
						<span
							id="result-layout-toggle-label"
							className="text-xs font-medium uppercase tracking-wide text-slate-500"
						>
							{redesign.layout_toggle_label}:
						</span>
						<div
							role="radiogroup"
							aria-labelledby="result-layout-toggle-label"
							className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs"
						>
							<LayoutOption
								value="feed"
								active={layout === "feed"}
								label={redesign.layout_feed}
								onChange={onLayoutChange}
							/>
							<LayoutOption
								value="master-detail"
								active={layout === "master-detail"}
								label={redesign.layout_master_detail}
								onChange={onLayoutChange}
							/>
						</div>
					</div>
				) : null}
			</div>
		</section>
	);
}

/**
 * Сегмент-кнопка layout-toggle: native `<input type="radio">` со скрытым input
 * + label-обёрткой для визуального стиля. Семантически корректен (a11y) и
 * стилистически выглядит как button — лучшее из обоих миров.
 */
function LayoutOption({
	value,
	active,
	label,
	onChange,
}: {
	value: ResultLayout;
	active: boolean;
	label: string;
	onChange: (layout: ResultLayout) => void;
}) {
	return (
		<label
			className={`cursor-pointer rounded px-2 py-0.5 transition-colors ${active ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
		>
			<input
				type="radio"
				name="result-layout"
				value={value}
				checked={active}
				onChange={() => onChange(value)}
				className="sr-only"
			/>
			{label}
		</label>
	);
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
			<dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
		</div>
	);
}

/**
 * Резолвит имя автора прогона (FR-017).
 *
 * Контракт фичи 022: `RunRecord.userId: number | null`. Поля `userName` в
 * контракте НЕТ (см. `@efes/shared/contracts/reconciliation.ts:RunRecordSchema`),
 * backend не модифицируется (FR-033). Стратегия в трёх шагах:
 *
 * 1. Если `userId === currentUser.id` — берём `displayName` из auth-store
 *    (типичный сценарий: пользователь смотрит СВОЙ прогон).
 * 2. Иначе показываем `userId` короткой подписью «Пользователь #N» —
 *    лучше чем «Неизвестен», потому что хотя бы идентифицирует автора.
 *    (Fetch на `/users/:id` — отдельная фича; backend сейчас не отдаёт имя.)
 * 3. Если `userId === null` (legacy записи до фичи 015) — fallback
 *    `unknown_user` (FR-017).
 */
function resolveUserName(
	run: RunRecord,
	currentUser: { id: number; displayName: string } | null,
): string {
	if (run.userId === null) return reconciliationMessages.unknown_user;
	if (currentUser && run.userId === currentUser.id) return currentUser.displayName;
	// Fallback: backend не отдаёт displayName для других userId. Не показываем
	// числовой id (UI noise) — оставляем «Неизвестен» до отдельной фичи /users/:id lookup.
	return reconciliationMessages.unknown_user;
}
